import React, { useEffect, useState } from "react";
import DateRangeChip from "../../common/DateRangeChip";
import { DateRangePreset } from "../../../types/reports";
import { ViewRange } from "../../../types/reportsV2";
import { dateRangeLabel, formatDateSpan } from "../../../utils/dateRangeFormat";
import { FIXED_DATES_PRESET } from "./model/viewConfig";

export interface ReportDateChipProps {
  range: ViewRange;
  /** The definition's presets (custom dates are always allowed). */
  presets: DateRangePreset[];
  /** Range the view starts with; any other range shows the chip as active. */
  defaultRange: ViewRange;
  /** Resolved dates of the last run, shown as a muted hint while the chip
   *  shows that run's preset. */
  resolved?: ResolvedRange | null;
  onChange: (range: ViewRange) => void;
}

export const sameRange = (a: ViewRange, b: ViewRange) =>
  a.preset === b.preset && a.start === b.start && a.end === b.end;

export type ResolvedRange = { preset: string; start: string; end: string };

/** The last run's resolved dates ("Oct 1 – 7"), only while the range shown
 *  is that run's preset (never for custom dates, which show themselves). */
export function resolvedHint(
  shown: ViewRange,
  resolved: ResolvedRange | null | undefined,
): string | undefined {
  return shown.preset !== FIXED_DATES_PRESET &&
    resolved?.preset === shown.preset
    ? formatDateSpan(resolved.start, resolved.end)
    : undefined;
}

/**
 * Date chip for a v2 report. A custom range is only applied once both dates
 * are picked; until then the chip keeps the half-picked range locally so the
 * report doesn't run with an incomplete range. Closing the menu, or any
 * change to the view's range (e.g. Discard), drops the half-picked range so
 * the chip always matches the data shown.
 */
export default function ReportDateChip({
  range,
  presets,
  defaultRange,
  resolved,
  onChange,
}: ReportDateChipProps) {
  const [pending, setPending] = useState<ViewRange | null>(null);
  const shown = pending ?? range;

  useEffect(() => {
    setPending(null);
  }, [range.preset, range.start, range.end]);

  const handleChange = (
    preset: DateRangePreset,
    start: string | null,
    end: string | null,
  ) => {
    if (preset !== FIXED_DATES_PRESET) {
      setPending(null);
      onChange({ preset });
      return;
    }
    if (start && end && start <= end) {
      setPending(null);
      onChange({ preset, start, end });
      return;
    }
    setPending({ preset, start: start ?? undefined, end: end ?? undefined });
  };

  const hint = pending ? undefined : resolvedHint(shown, resolved);

  return (
    <DateRangeChip
      variant="outline"
      preset={shown.preset}
      customStart={shown.start ?? null}
      customEnd={shown.end ?? null}
      presets={presets}
      hint={hint}
      active={!sameRange(range, defaultRange)}
      ariaLabel={`Date range: ${dateRangeLabel(
        shown.preset,
        shown.start ?? null,
        shown.end ?? null,
      )}`}
      onChange={handleChange}
      onOpenChange={(opened) => {
        if (!opened) setPending(null);
      }}
    />
  );
}
